/**
 * Calc reconciliation strip (DATA_VISUALIZATION_FRONTEND_PLAN §2.2).
 *
 * Shows the Σ-daily total of a series next to the calc engine's period figure
 * for the same window, so a reviewer can compare them. Both are
 * server-authored decimal strings rendered as-is — the UI never recomputes a
 * compliance figure (locked decision #6), so it does NOT subtract them into a
 * client-side variance. A reconciliation verdict/delta, if needed, is the
 * backend's to author (and audit); here we present only the two source values.
 */
import { Badge, Group, Paper, SimpleGrid, Stack, Text } from "@mantine/core";
import { IconScale } from "@tabler/icons-react";
import { formatCell } from "./series-data";
import { unitLabel } from "./units";

export type ReconRow = {
  key: string;
  /** Series whose daily values are summed (e.g. "Hub inlet energy"). */
  label: string;
  unit: string;
  /** Σ of daily points (the series' server total), or null if not loaded. */
  dailyTotal: string | null;
  /**
   * The strip's own pull for this row failed.
   *
   * Distinct from "not loaded yet", and the distinction is the point: a failed
   * reconciliation read that renders as "pending" is indistinguishable from a
   * slow one, so a reviewer waits instead of retrying and the comparison they
   * came for silently never arrives.
   */
  failed?: boolean;
  /** The calc engine's period figure, or null if not computed yet. */
  calcValue: string | null;
  /** Label of the calc figure (e.g. "Token MMBtu HHV"). */
  calcLabel: string;
};

export function CalcOverlayLegend({ rows }: { rows: ReconRow[] }) {
  return (
    <Paper withBorder p="md">
      <Group gap={8} mb="sm">
        <IconScale size={18} />
        <Text size="sm" fw={600}>
          Calc reconciliation
        </Text>
        <Text size="xs" c="dimmed">
          Σ daily vs calc-engine period figure
        </Text>
      </Group>
      <SimpleGrid cols={{ base: 1, md: rows.length > 1 ? 2 : 1 }} spacing="md">
        {rows.map((row) => {
          const loaded = row.dailyTotal != null && row.calcValue != null;
          return (
            <Paper key={row.key} withBorder p="sm" radius="md">
              <Stack gap={6}>
                <Group justify="space-between" wrap="nowrap">
                  <Text size="sm" fw={500}>
                    {row.label}
                  </Text>
                  {row.failed ? (
                    <Badge color="red" variant="light" size="sm">
                      failed to load
                    </Badge>
                  ) : loaded ? null : (
                    <Badge color="gray" variant="light" size="sm">
                      pending
                    </Badge>
                  )}
                </Group>
                <Group justify="space-between" gap="xl" wrap="nowrap">
                  <Stack gap={0}>
                    <Text size="xs" c="dimmed">
                      Σ daily
                    </Text>
                    <Text size="sm" ff="monospace">
                      {formatCell(row.dailyTotal, 2)} {unitLabel(row.unit)}
                    </Text>
                  </Stack>
                  <Stack gap={0} ta="right">
                    <Text size="xs" c="dimmed">
                      {row.calcLabel}
                    </Text>
                    <Text size="sm" ff="monospace">
                      {formatCell(row.calcValue, 2)} {unitLabel(row.unit)}
                    </Text>
                  </Stack>
                </Group>
              </Stack>
            </Paper>
          );
        })}
      </SimpleGrid>
    </Paper>
  );
}
